"use client";

import React, { useState } from "react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/feedback";
import { Button } from "@/components/ui/button";
import { Sparkles, ArrowRight, Check, Loader2 } from "lucide-react";

interface SwotItem {
  id: string;
  type: "strength" | "weakness" | "opportunity" | "threat";
  title: string;
  description: string;
  sourceNodes: string[];
  createdAt: string;
}

interface AiQuadrantProps {
  items: SwotItem[];
  onApplySuggestion: (
    type: "strength" | "weakness" | "opportunity" | "threat",
    title: string,
    description: string,
  ) => Promise<{ success?: boolean; error?: string }>;
}

export function AiQuadrant({ items, onApplySuggestion }: AiQuadrantProps) {
  const [applyingId, setApplyingId] = useState<string | null>(null);
  const [appliedIds, setAppliedIds] = useState<Set<string>>(new Set());

  // Filter items
  const strengths = items.filter((i) => i.type === "strength");
  const weaknesses = items.filter((i) => i.type === "weakness");
  const opportunities = items.filter((i) => i.type === "opportunity");
  const threats = items.filter((i) => i.type === "threat");

  // Dynamic Suggestion Builder
  const getSuggestions = () => {
    const list = [];

    // 1. S-O Strategy
    if (strengths.length > 0 && opportunities.length > 0) {
      const s = strengths[0];
      const o = opportunities[0];
      list.push({
        id: "so_1",
        strategy: "Strategia Offensiva (S-O)",
        title: `Sfrutta ${s.title}`,
        description: `Utilizza il punto di forza "${s.title}" per capitalizzare ed accelerare sull'opportunità esterna "${o.title}".`,
        targetType: "opportunity" as const,
        targetTitle: `Sinergia: ${s.title} & ${o.title}`,
        targetDesc: `Analisi strategica: La nostra forza in "${s.title}" ci posiziona perfettamente per catturare l'opportunità "${o.title}". Raccomandato l'avvio immediato di un team dedicato.`,
      });
    } else {
      list.push({
        id: "so_default",
        strategy: "Strategia Offensiva (S-O)",
        title: "Sinergia Competenze & Nuovi Mercati",
        description:
          "Sfrutta l'elevata flessibilità del team interno per lanciare moduli aggiuntivi SaaS ad alto margine prima dei concorrenti.",
        targetType: "opportunity" as const,
        targetTitle: "Sviluppo Moduli SaaS a Margine Elevato",
        targetDesc:
          "Utilizzare la velocità di rilascio del team di sviluppo per conquistare quote di mercato ad alto rendimento prima che i competitor si adeguino.",
      });
    }

    // 2. W-O Strategy
    if (weaknesses.length > 0 && opportunities.length > 0) {
      const w = weaknesses[0];
      const o = opportunities[0];
      list.push({
        id: "wo_1",
        strategy: "Strategia di Adattamento (W-O)",
        title: `Supera ${w.title}`,
        description: `Riduci o correggi la debolezza interna "${w.title}" per sbloccare completamente l'opportunità di mercato "${o.title}".`,
        targetType: "opportunity" as const,
        targetTitle: `Ottimizzazione: Risoluzione ${w.title}`,
        targetDesc: `Risolvere le problematiche relative a "${w.title}" è fondamentale per non perdere i benefici legati a "${o.title}".`,
      });
    } else {
      list.push({
        id: "wo_default",
        strategy: "Strategia di Adattamento (W-O)",
        title: "Mitigazione Debito Tecnico per Partnership",
        description:
          "Risolvi i rallentamenti del codice legacy per velocizzare le integrazioni API esterne con i partner commerciali.",
        targetType: "opportunity" as const,
        targetTitle: "Integrazioni API Veloci per Partner B2B",
        targetDesc:
          "Modernizzare i moduli legacy chiave per consentire l'onboarding di partner in meno di 24 ore, raddoppiando il canale di lead.",
      });
    }

    // 3. S-T Strategy
    if (strengths.length > 0 && threats.length > 0) {
      const s = strengths[0];
      const t = threats[0];
      list.push({
        id: "st_1",
        strategy: "Strategia Difensiva (S-T)",
        title: `Scudo: ${s.title}`,
        description: `Impiega il punto di forza "${s.title}" per difenderti o neutralizzare la minaccia esterna "${t.title}".`,
        targetType: "threat" as const,
        targetTitle: `Scudo Competitivo: ${s.title}`,
        targetDesc: `La nostra forza in "${s.title}" ci protegge direttamente dagli impatti negativi derivanti da "${t.title}".`,
      });
    } else {
      list.push({
        id: "st_default",
        strategy: "Strategia Difensiva (S-T)",
        title: "Protezione Clientela via Brand Trust",
        description:
          "Consolida la forte relazione di fiducia e supporto con i clienti attuali per arginare la minaccia di nuovi concorrenti low-cost.",
        targetType: "threat" as const,
        targetTitle: "Customer Retention vs Competitor Low-Cost",
        targetDesc:
          "Rafforzare i contratti di manutenzione e i canali di assistenza dedicati per annullare l'attrattiva dei concorrenti basati solo sul prezzo.",
      });
    }

    return list;
  };

  const handleApply = async (
    id: string,
    type: "strength" | "weakness" | "opportunity" | "threat",
    title: string,
    description: string,
  ) => {
    setApplyingId(id);
    try {
      const res = await onApplySuggestion(type, title, description);
      if (res.success) {
        setAppliedIds((prev) => {
          const next = new Set(prev);
          next.add(id);
          return next;
        });
      } else if (res.error) {
        alert(res.error);
      }
    } catch (err) {
      alert("Errore durante l'applicazione del suggerimento.");
    } finally {
      setApplyingId(null);
    }
  };

  const suggestions = getSuggestions();

  return (
    <Card className="bg-card border border-border shadow-sm">
      <CardHeader className="pb-2 border-b border-border/50 flex flex-row items-center justify-between">
        <CardTitle className="text-sm font-bold flex items-center gap-2">
          <Sparkles className="h-4.5 w-4.5 text-yellow-500 animate-pulse" />
          Strategie SWOT Suggerite dall&apos;AI
        </CardTitle>
        <Badge className="bg-primary-500/10 text-primary-600 border border-primary-500/20 text-[10px] font-semibold">
          Analisi Matrice Attiva
        </Badge>
      </CardHeader>
      <CardContent className="p-6">
        <p className="text-xs text-text-secondary mb-5">
          L&apos;intelligenza artificiale ha incrociato i fattori interni ed
          esterni per strutturare piani d&apos;azione immediati. Clicca su
          &quot;Applica&quot; per importarli direttamente nell&apos;analisi
          strategica.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {suggestions.map((s) => {
            const isApplied = appliedIds.has(s.id);
            const isApplying = applyingId === s.id;

            return (
              <div
                key={s.id}
                className="flex flex-col justify-between p-4 rounded-xl border border-border/60 bg-card-muted/30 hover:bg-card-muted/50 transition-all space-y-4"
              >
                <div className="space-y-2">
                  <div className="flex justify-between items-start">
                    <span className="text-[10px] uppercase font-bold text-primary-600 tracking-wider">
                      {s.strategy}
                    </span>
                  </div>
                  <h4 className="text-xs font-bold text-text-primary leading-tight">
                    {s.title}
                  </h4>
                  <p className="text-[10px] text-text-secondary leading-relaxed">
                    {s.description}
                  </p>
                </div>

                <div className="pt-2 border-t border-border/40 flex items-center justify-between">
                  <span className="text-[9px] text-text-secondary italic">
                    Aggiunge come:{" "}
                    <b className="text-text-primary font-bold">
                      {s.targetType}
                    </b>
                  </span>

                  <Button
                    variant={isApplied ? "outline" : "default"}
                    size="sm"
                    className="h-7 text-[10px] px-2 rounded-md font-semibold"
                    disabled={isApplied || isApplying}
                    onClick={() =>
                      handleApply(
                        s.id,
                        s.targetType,
                        s.targetTitle,
                        s.targetDesc,
                      )
                    }
                  >
                    {isApplying ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : isApplied ? (
                      <span className="flex items-center gap-0.5 text-emerald-600 font-bold">
                        <Check className="h-3.5 w-3.5" /> Applicato
                      </span>
                    ) : (
                      <span className="flex items-center gap-0.5">
                        Applica <ArrowRight className="h-3 w-3" />
                      </span>
                    )}
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
